// src/components/AuthVerificationScreen.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { FiCheckCircle, FiMail, FiRefreshCw } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

const AuthVerificationScreen = () => {
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const { user, reloadAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        console.log('📧 Verifying email confirmation status...');
        
        // انتظر قليلاً للتأكد من تحديث حالة البريد المؤكد
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // إعادة تحميل البيانات
        await reloadAuth();
        
        // تحقق من تأكيد البريد
        const { data: { user: currentUser }, error } = await supabaseService.client.auth.getUser();
        
        if (error) {
          console.error('❌ Error checking email verification:', error);
          setVerificationStatus('error');
          setIsVerifying(false);
          return;
        }

        if (currentUser?.email_confirmed_at || currentUser?.user_metadata?.email_verified) {
          console.log('✅ Email is confirmed!');
          setVerificationStatus('success');
          
          // الانتقال إلى صفحة التسجيل مباشرة
          setTimeout(() => {
            navigate('/register');
          }, 2000);
        } else {
          console.log('⏳ Waiting for email confirmation...');
          setVerificationStatus('pending');
        }
        
        setIsVerifying(false);
      } catch (error) {
        console.error('❌ Verification error:', error);
        setVerificationStatus('error');
        setIsVerifying(false);
      }
    };

    if (user) {
      verifyEmail();
    }
  }, [user, navigate, reloadAuth]);

  const handleResendEmail = async () => {
    if (!user?.email) {
      toast.error('لم نتمكن من العثور على بريدك الإلكتروني');
      return;
    }

    try {
      setIsVerifying(true);
      
      console.log('📧 Resending confirmation email to:', user.email);
      
      const { error } = await supabaseService.client.auth.resendEnvelope({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/register`
        }
      });

      if (error) {
        console.error('❌ Resend error:', error);
        throw error;
      }

      console.log('✅ Confirmation email resent');
      toast.success('✅ تم إعادة إرسال بريد التأكيد.\nتحقق من صندوق البريد الخاص بك (وقسم الرسائل المزعجة أيضاً)', {
        duration: 5000
      });
    } catch (error) {
      console.error('❌ Error resending email:', error);
      toast.error(error.message || 'فشل إعادة إرسال البريد');
    } finally {
      setIsVerifying(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-gray-600 mb-4">جاري التحقق من بيانات الجلسة...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-khidma-gray to-gray-200 flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gray-900 text-white p-8 text-center">
            {verificationStatus === 'pending' && (
              <>
                <div className="mb-4 flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 animate-ping rounded-full bg-blue-100 opacity-75"></div>
                    <div className="relative animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-400"></div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold">تأكيد البريد الإلكتروني</h2>
              </>
            )}

            {verificationStatus === 'success' && (
              <>
                <div className="mb-4 flex justify-center">
                  <FiCheckCircle className="text-green-400 text-6xl" />
                </div>
                <h2 className="text-2xl font-bold">تم تأكيد البريد بنجاح!</h2>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <div className="mb-4 flex justify-center">
                  <FiMail className="text-red-400 text-6xl" />
                </div>
                <h2 className="text-2xl font-bold">خطأ في التحقق</h2>
              </>
            )}
          </div>

          <div className="p-8">
            {verificationStatus === 'pending' && (
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-700 mb-2">
                    لقد أرسلنا بريد تأكيد إلى:
                  </p>
                  <p className="text-lg font-bold text-gray-900">{user?.email}</p>
                  <p className="text-gray-600 text-sm mt-4">
                    افتح البريد واضغط على رابط التأكيد لمتابعة التسجيل
                  </p>
                </div>

                <button
                  onClick={handleResendEmail}
                  disabled={isVerifying}
                  className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mb-4"
                >
                  {isVerifying ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw className="ml-2" />
                      إعادة إرسال بريد التأكيد
                    </>
                  )}
                </button>

                <p className="text-gray-600 text-sm text-center">
                  لم تستقبل البريد؟ تحقق من قسم الرسائل المزعجة (Spam Folder)
                </p>
              </>
            )}

            {verificationStatus === 'success' && (
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-700 mb-4">
                    سيتم نقلك إلى صفحة إكمال البيانات تلقائياً...
                  </p>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                </div>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-700 mb-4">
                    حدث خطأ في التحقق من البريد
                  </p>
                  <button
                    onClick={handleResendEmail}
                    disabled={isVerifying}
                    className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isVerifying ? 'جاري الإرسال...' : 'إعادة إرسال البريد'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthVerificationScreen;